import { SurveyMiscAttributes } from '@ardoq/api-types';
import {
  getSurveyErrorNameMessage,
  getSurveyWarningNameMessage,
  SurveyErrorName,
  SurveySectionId,
  SurveyWarningName,
} from './consts';
import { SurveyValidation } from './types';
import { type I18nLocale } from '@ardoq/i18n';

const hasError = (validation: SurveyValidation | undefined): boolean => {
  return !!validation && validation.errorCodes.size > 0;
};

const hasWarning = (validation: SurveyValidation | undefined): boolean => {
  return !!validation && validation.warningCodes.size > 0;
};

const containsErrorId = (
  validation: SurveyValidation | undefined,
  errorId: SurveyErrorName
): boolean => {
  return containsAnyErrorId(validation, [errorId]);
};

const containsWarningId = (
  validation: SurveyValidation | undefined,
  warningId: SurveyWarningName
): boolean => {
  return !!validation && validation.warningCodes.has(warningId);
};

const containsAnyErrorId = (
  validation: SurveyValidation | undefined,
  errorIds: SurveyErrorName[]
): boolean => {
  return (
    !!validation && errorIds.some(errorId => validation.errorCodes.has(errorId))
  );
};

const sectionHasError = (
  validation: SurveyValidation | undefined,
  sectionId: SurveySectionId
): boolean => {
  return validation
    ? sectionMappedErrorNames[sectionId].some(errorId =>
        validation.errorCodes.has(errorId)
      )
    : false;
};

const sectionHasWarning = (
  validation: SurveyValidation | undefined,
  sectionId: SurveySectionId
): boolean => {
  return validation
    ? sectionMappedWarningNames[sectionId].some(warningId =>
        validation.warningCodes.has(warningId)
      )
    : false;
};

const getWarningMessageIfExists = (
  validation: SurveyValidation | undefined,
  warningId: SurveyWarningName,
  i18nLocale: I18nLocale
): string | undefined =>
  validation?.warningCodes.has(warningId)
    ? getSurveyWarningNameMessage(warningId, i18nLocale)
    : undefined;

const getOverviewErrorMessages = (
  validation: SurveyValidation,
  i18nLocale: I18nLocale
): string[] =>
  Array.from(validation.errorCodes).map(code =>
    getSurveyErrorNameMessage(code, i18nLocale)
  );

const getOverviewWarningMessages = (
  validation: SurveyValidation,
  i18nLocale: I18nLocale
): string[] =>
  Array.from(validation.warningCodes).map(code =>
    getSurveyWarningNameMessage(code, i18nLocale)
  );

const getErrorMessageIfExists = (
  validation: SurveyValidation | undefined,
  errorId: SurveyErrorName,
  i18nLocale: I18nLocale
): string | undefined =>
  validation?.errorCodes.has(errorId)
    ? getSurveyErrorNameMessage(errorId, i18nLocale)
    : undefined;

const hasVisibleError = (validation: SurveyValidation): boolean => {
  return Object.entries(sectionStatusPropertyBySectionId).some(
    ([sectionId, statusProp]) =>
      sectionHasError(validation, sectionId as SurveySectionId) &&
      (validation.misc[statusProp] ?? 'left') === 'left'
  );
};

export const validationHelpers = {
  hasError,
  hasWarning,
  containsErrorId,
  containsWarningId,
  containsAnyErrorId,
  sectionHasError,
  sectionHasWarning,
  getOverviewErrorMessages,
  getOverviewWarningMessages,
  getErrorMessageIfExists,
  getWarningMessageIfExists,
  hasVisibleError,
};

const sectionStatusPropertyBySectionId: Record<
  SurveySectionId,
  keyof SurveyMiscAttributes
> = {
  [SurveySectionId.GENERAL_INFORMATION]: 'generalInformationSectionStatus',
  [SurveySectionId.DEFINE_THE_DATA]: 'defineTheDataSectionStatus',
  [SurveySectionId.QUESTIONS]: 'questionsSectionStatus',
  [SurveySectionId.RESULTS_DISPLAY]: 'resultsDisplaySectionStatus',
  [SurveySectionId.CHANGE_APPROVAL]: 'changeApprovalSectionStatus',
};

const sectionMappedErrorNames: Record<SurveySectionId, SurveyErrorName[]> = {
  [SurveySectionId.GENERAL_INFORMATION]: [
    SurveyErrorName.NAME_INVALID,
    SurveyErrorName.CONTACT_EMAIL_INVALID,
  ],
  [SurveySectionId.DEFINE_THE_DATA]: [
    SurveyErrorName.WORKSPACE_INVALID,
    SurveyErrorName.COMPONENT_TYPE_INVALID,
    SurveyErrorName.REQUIRED_NEW_COMPONENT_PARENT_IS_MISSING,
    SurveyErrorName.SELECTED_NEW_COMPONENT_PARENT_DOES_NOT_EXIST,
    SurveyErrorName.NO_ADMIN_ACCESS_TO_SURVEY_WORKSPACE,
  ],
  [SurveySectionId.QUESTIONS]: [
    SurveyErrorName.HIDDEN_FIELDS,
    SurveyErrorName.MISSING_NAME_QUESTION,
    SurveyErrorName.REQUIRED_NEW_COMPONENT_PARENT_IS_MISSING,
    SurveyErrorName.QUESTION_ERRORS_EXIST,
  ],
  [SurveySectionId.RESULTS_DISPLAY]: [
    SurveyErrorName.MISSING_RESULT_FILTERING_FIELDS,
    SurveyErrorName.SELECTED_RESULT_FILTERING_COMPONENT_TYPE_INVALID,
    SurveyErrorName.SELECTED_RESULT_FILTERING_REFERENCE_TYPE_INVALID,
  ],
  [SurveySectionId.CHANGE_APPROVAL]: [
    SurveyErrorName.CHANGE_APPROVAL_COMPATIBILITY,
    SurveyErrorName.CHANGE_APPROVAL_FALLBACK_USER,
    SurveyErrorName.CHANGE_APPROVAL_INVALID_ADVANCED_AUDIENCE,
  ],
};

const sectionMappedWarningNames: Record<SurveySectionId, SurveyWarningName[]> =
  {
    [SurveySectionId.GENERAL_INFORMATION]: [],
    [SurveySectionId.DEFINE_THE_DATA]: [],
    [SurveySectionId.CHANGE_APPROVAL]: [
      SurveyWarningName.CHANGE_APPROVAL_EMPTY_USER_GROUP,
    ],
    [SurveySectionId.RESULTS_DISPLAY]: [
      SurveyWarningName.NO_ACCESS_TO_SELECTED_RESULT_FILTERING_WORKSPACE,
    ],
    [SurveySectionId.QUESTIONS]: [SurveyWarningName.QUESTION_WARNINGS_EXIST],
  };
